<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import subDays from 'date-fns/subDays';
import BarChart from 'shared/components/charts/BarChart.vue';
import { bucketLabel } from '../../helper/crmBuckets';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import CrmDealsReportsAPI from 'dashboard/api/reports/crmDeals';

const { t } = useI18n();

const DATE_FILTERS = {
  TODAY: 'today',
  LAST_WEEK: 'last_week',
  LAST_MONTH: 'last_month',
  CUSTOM: 'custom'
};

const selectedDateFilter = ref(null);
const customDateRange = ref([new Date(), new Date()]);
const isLoading = ref(false);
// Agregados calculados en el backend (V2::Reports::Crm::DealsBuilder), acotados al rango.
const summary = ref(null);

const dateFilterOptions = computed(() => [
  { id: DATE_FILTERS.TODAY, name: t('CRM_REPORTS.LEADS.FILTERS.TODAY') },
  { id: DATE_FILTERS.LAST_WEEK, name: t('CRM_REPORTS.LEADS.FILTERS.LAST_WEEK') },
  { id: DATE_FILTERS.LAST_MONTH, name: t('CRM_REPORTS.LEADS.FILTERS.LAST_MONTH') },
  { id: DATE_FILTERS.CUSTOM, name: t('CRM_REPORTS.LEADS.FILTERS.CUSTOM') }
]);

onMounted(() => {
  selectedDateFilter.value = dateFilterOptions.value[0];
});

const dateRange = computed(() => {
  if (!selectedDateFilter.value) return { start: new Date(), end: new Date() };
  
  const today = new Date();
  
  switch (selectedDateFilter.value.id) {
    case DATE_FILTERS.TODAY:
      return {
        start: startOfDay(today),
        end: endOfDay(today)
      };
    case DATE_FILTERS.LAST_WEEK:
      return {
        start: startOfDay(subDays(today, 7)),
        end: endOfDay(today)
      };
    case DATE_FILTERS.LAST_MONTH:
      return {
        start: startOfDay(subDays(today, 30)),
        end: endOfDay(today)
      };
    case DATE_FILTERS.CUSTOM:
      return {
        start: startOfDay(customDateRange.value[0]),
        end: endOfDay(customDateRange.value[1])
      };
    default:
      return {
        start: startOfDay(today),
        end: endOfDay(today)
      };
  }
});

const fetchSummary = async () => {
  const { start, end } = dateRange.value;
  isLoading.value = true;
  try {
    const { data } = await CrmDealsReportsAPI.getSummary({
      since: Math.floor(start.getTime() / 1000),
      until: Math.floor(end.getTime() / 1000),
      sections: ['won', 'by_agent']
    });
    summary.value = data;
  } catch (error) {
    summary.value = null;
  } finally {
    isLoading.value = false;
  }
};

watch(dateRange, fetchSummary);

// Venta = negocio con status `won`, fechado por su cierre. Antes se tomaba cualquier deal
// con `closed_at`, que también incluía perdidos y cancelados sin stage ganado.
const won = computed(() => summary.value?.won || {});

// Granularidad elegida por el backend según el largo del rango (día / semana / mes).
const granularity = computed(() => summary.value?.range?.granularity || 'day');
const labelFor = timestamp => bucketLabel(timestamp, granularity.value);

const totalSales = computed(() => won.value.amounts || { ars: 0, usd: 0 });
const totalSalesCount = computed(() => won.value.count ?? 0);

// Helper para formatear números con separadores de miles
const formatCurrency = (value, currency) => {
  const formatted = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);

  return currency === 'ars' ? `$${formatted}` : `US$${formatted}`;
};

const formattedTotalARS = computed(() => formatCurrency(totalSales.value.ars, 'ars'));
const formattedTotalUSD = computed(() => formatCurrency(totalSales.value.usd, 'usd'));

// ===== TICKET PROMEDIO =====

const averageTicket = computed(() => won.value.avg_ticket || { ars: 0, usd: 0 });

const formattedAvgTicketARS = computed(() => formatCurrency(averageTicket.value.ars, 'ars'));
const formattedAvgTicketUSD = computed(() => formatCurrency(averageTicket.value.usd, 'usd'));

// Ticket promedio por bucket: se muestra ARS y, si no hubo ventas en pesos, el de dólares.
const averageTicketChartData = computed(() => {
  const points = won.value.avg_ticket_series || [];
  return {
    labels: points.map(p => labelFor(p.timestamp)),
    datasets: [
      {
        label: t('CRM_REPORTS.SALES.CHART.AVG_TICKET'),
        data: points.map(p => Math.round(p.ars || p.usd || 0)),
        backgroundColor: 'rgb(59, 130, 246)',
        borderColor: 'rgb(37, 99, 235)',
        borderWidth: 1
      }
    ]
  };
});

const salesChartData = computed(() => {
  const points = won.value.series || [];
  return {
    labels: points.map(p => labelFor(p.timestamp)),
    datasets: [
      {
        label: t('CRM_REPORTS.SALES.CHART.SALES'),
        data: points.map(p => p.value),
        backgroundColor: 'rgb(59, 130, 246)',
        borderColor: 'rgb(37, 99, 235)',
        borderWidth: 1
      }
    ]
  };
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      enabled: true
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1
      }
    }
  }
};

const onDateRangeChange = (value) => {
  customDateRange.value = value;
};

watch(selectedDateFilter, (newValue) => {
  if (newValue && newValue.id !== DATE_FILTERS.CUSTOM) {
    customDateRange.value = [new Date(), new Date()];
  }
}, { deep: true });

// ===== AGENT PARTICIPATION SECTION =====
// El ranking sale agrupado por owner del negocio ganado; la participación se calcula
// contra el total facturado del rango (denominador que la tabla también muestra).
const totalBilling = computed(() => totalSales.value);

const agentParticipationData = computed(() => {
  const totals = totalSales.value;
  const share = (value, total) => (total > 0 ? Math.round((value / total) * 1000) / 10 : 0);

  return (summary.value?.by_agent || []).map(agent => ({
    agentId: agent.agent_id,
    agentName: agent.agent_name,
    agentEmail: agent.agent_email,
    totalArs: agent.ars,
    totalUsd: agent.usd,
    participationArs: share(agent.ars, totals.ars),
    participationUsd: share(agent.usd, totals.usd)
  }));
});

// Computed - Chart data para participación por agente
const agentParticipationChartData = computed(() => {
  const labels = agentParticipationData.value.map(agent => agent.agentName);
  const arsData = agentParticipationData.value.map(agent => agent.participationArs);
  const usdData = agentParticipationData.value.map(agent => agent.participationUsd);
  
  return {
    labels,
    datasets: [
      {
        label: 'Participación ARS (%)',
        data: arsData,
        backgroundColor: 'rgba(34, 197, 94, 0.7)', // green-500
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 1
      },
      {
        label: 'Participación USD (%)',
        data: usdData,
        backgroundColor: 'rgba(59, 130, 246, 0.7)', // blue-500
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1
      }
    ]
  };
});

// Chart options para gráfico de participación
const participationChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      enabled: true,
      callbacks: {
        label: (context) => {
          return `${context.dataset.label}: ${context.parsed.y.toFixed(1)}%`;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
      ticks: {
        callback: (value) => `${value}%`
      }
    }
  }
};
</script>

<template>
  <div class="space-y-6">
    <div class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ $t('CRM_REPORTS.SALES.TITLE') }}
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {{ $t('CRM_REPORTS.SALES.DESCRIPTION') }}
          </p>
        </div>
        
        <div class="flex items-center gap-4">
          <div class="multiselect-wrap--small w-48">
            <multiselect
              v-model="selectedDateFilter"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT_ONE')"
              :options="dateFilterOptions"
              :searchable="false"
              :allow-empty="false"
              :show-labels="false"
            />
          </div>
          
          <WootDateRangePicker
            v-if="selectedDateFilter && selectedDateFilter.id === DATE_FILTERS.CUSTOM"
            :value="customDateRange"
            :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
            :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
            @change="onDateRangeChange"
          />
        </div>
      </div>
      
      <!-- Métricas -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <!-- Total de Ventas Cerradas -->
        <div class="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-slate-600 dark:text-slate-400">
                {{ $t('CRM_REPORTS.SALES.METRICS.TOTAL_SALES.LABEL') }}
              </p>
              <p class="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">
                {{ totalSalesCount }}
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{ $t('CRM_REPORTS.SALES.METRICS.TOTAL_SALES.SUBTITLE') }}
              </p>
            </div>
            <div class="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <fluent-icon icon="money-hand" size="24" class="text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>
        
        <!-- Valor Total de Ventas -->
        <div class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
          <div class="flex items-start justify-between mb-2">
            <div class="flex items-center gap-2">
              <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ $t('CRM_REPORTS.SALES.METRICS.TOTAL_VALUE.LABEL') }}
              </h4>
              <button
                class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                :title="$t('CRM_REPORTS.SALES.METRICS.TOTAL_VALUE.INFO')"
              >
                <fluent-icon icon="info" size="16" />
              </button>
            </div>
            <div class="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <fluent-icon icon="money" size="20" class="text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          
          <div class="space-y-2 mt-3">
            <!-- Pesos Argentinos -->
            <div class="flex items-baseline justify-between">
              <span class="text-xs text-slate-600 dark:text-slate-400 font-medium">ARS</span>
              <span class="text-lg font-bold text-slate-900 dark:text-slate-100">
                {{ formattedTotalARS }}
              </span>
            </div>
            
            <!-- Dólares -->
            <div class="flex items-baseline justify-between">
              <span class="text-xs text-slate-600 dark:text-slate-400 font-medium">USD</span>
              <span class="text-lg font-bold text-slate-900 dark:text-slate-100">
                {{ formattedTotalUSD }}
              </span>
            </div>
          </div>
          
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-3">
            {{ $t('CRM_REPORTS.SALES.METRICS.TOTAL_VALUE.SUBTITLE') }}
          </p>
        </div>
        
        <!-- Ticket Promedio -->
        <div class="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-700">
          <div class="flex items-start justify-between mb-2">
            <div class="flex items-center gap-2">
              <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ $t('CRM_REPORTS.SALES.METRICS.AVG_TICKET.LABEL') }}
              </h4>
              <button
                class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                :title="$t('CRM_REPORTS.SALES.METRICS.AVG_TICKET.INFO')"
              >
                <fluent-icon icon="info" size="16" />
              </button>
            </div>
            <div class="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
              <fluent-icon icon="receipt" size="20" class="text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>
          
          <div class="space-y-2 mt-3">
            <!-- Pesos Argentinos -->
            <div class="flex items-baseline justify-between">
              <span class="text-xs text-slate-600 dark:text-slate-400 font-medium">ARS</span>
              <span class="text-lg font-bold text-slate-900 dark:text-slate-100">
                {{ formattedAvgTicketARS }}
              </span>
            </div>
            
            <!-- Dólares -->
            <div class="flex items-baseline justify-between">
              <span class="text-xs text-slate-600 dark:text-slate-400 font-medium">USD</span>
              <span class="text-lg font-bold text-slate-900 dark:text-slate-100">
                {{ formattedAvgTicketUSD }}
              </span>
            </div>
          </div>
          
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-3">
            {{ $t('CRM_REPORTS.SALES.METRICS.AVG_TICKET.SUBTITLE') }}
          </p>
        </div>
      </div>
      
      <!-- Gráfico de Ventas por Día -->
      <div class="mt-6">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          {{ $t('CRM_REPORTS.SALES.CHART.TITLE') }}
        </h4>
        <div class="h-80">
          <BarChart v-if="totalSalesCount > 0" :collection="salesChartData" :chart-options="chartOptions" />
          <div v-else class="flex items-center justify-center h-full text-slate-500 dark:text-slate-400">
            No hay datos de ventas para el período seleccionado
          </div>
        </div>
      </div>
      
      <!-- Gráfico de Ticket Promedio por Día -->
      <div class="mt-6">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          {{ $t('CRM_REPORTS.SALES.CHART.AVG_TICKET_TITLE') }}
        </h4>
        <div class="h-80">
          <BarChart v-if="totalSalesCount > 0" :collection="averageTicketChartData" :chart-options="chartOptions" />
          <div v-else class="flex items-center justify-center h-full text-slate-500 dark:text-slate-400">
            No hay datos de ventas para el período seleccionado
          </div>
        </div>
      </div>
    </div>

    <!-- Participación por Agente sobre Facturación Total -->
    <div class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
      <div class="mb-6">
        <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Participación por Agente sobre Facturación Total
        </h3>
        <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
          Muestra el porcentaje de participación de cada agente sobre la facturación total del período seleccionado
        </p>
      </div>

      <!-- Gráfico de barras -->
      <div v-if="agentParticipationData.length > 0" class="mb-8">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          Distribución de Participación por Agente
        </h4>
        <div class="h-80">
          <BarChart :collection="agentParticipationChartData" :chart-options="participationChartOptions" />
        </div>
      </div>

      <!-- Mensaje si no hay datos -->
      <div v-else class="flex items-center justify-center h-40 text-slate-500 dark:text-slate-400">
        No hay datos de ventas para el período seleccionado
      </div>

      <!-- Tabla detallada -->
      <div v-if="agentParticipationData.length > 0" class="mt-8">
        <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
          Detalle por Agente
        </h4>
        <div class="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
          <table class="w-full">
            <thead class="bg-slate-50 dark:bg-slate-800">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Agente
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Ventas ARS
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Participación ARS
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Ventas USD
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Participación USD
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200 dark:divide-slate-700 bg-white dark:bg-slate-900">
              <tr 
                v-for="agentData in agentParticipationData" 
                :key="agentData.agentId"
                class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <td class="px-6 py-4">
                  <div class="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {{ agentData.agentName }}
                  </div>
                  <div class="text-xs text-slate-500 dark:text-slate-400">
                    {{ agentData.agentEmail }}
                  </div>
                </td>
                <td class="px-6 py-4 text-sm text-slate-600 dark:text-slate-400 text-right tabular-nums">
                  {{ new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(agentData.totalArs) }}
                </td>
                <td class="px-6 py-4 text-right">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                    {{ agentData.participationArs.toFixed(1) }}%
                  </span>
                </td>
                <td class="px-6 py-4 text-sm text-slate-600 dark:text-slate-400 text-right tabular-nums">
                  {{ new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(agentData.totalUsd) }}
                </td>
                <td class="px-6 py-4 text-right">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400">
                    {{ agentData.participationUsd.toFixed(1) }}%
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot class="bg-slate-50 dark:bg-slate-800">
              <tr>
                <td class="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Total
                </td>
                <td class="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100 text-right tabular-nums">
                  {{ new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(totalBilling.ars) }}
                </td>
                <td class="px-6 py-4 text-sm font-semibold text-green-600 dark:text-green-400 text-right">
                  100%
                </td>
                <td class="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100 text-right tabular-nums">
                  {{ new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalBilling.usd) }}
                </td>
                <td class="px-6 py-4 text-sm font-semibold text-blue-600 dark:text-blue-400 text-right">
                  100%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
